/**
 * Copyright: ThoughtSpot Inc. 2020
 * Author: Thawsitt Naing (thawsitt.naing@thoughtspot.com)
 *
 * @fileoverview Banner Alert for Radiant
 */
import { useEmbedContext } from '@thoughtspot/blink-context';
import { UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { trackException } from '@thoughtspot/observability';
import { getCurrentOrgId, getUserGuid, getUserPrivileges, } from '@thoughtspot/session-service';
import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../button';
import { Icon, IconColor, IconID, IconSize } from '../../icon';
import { Typography } from '../../typography';
import { CLOSE_BTN_ARIA_LABEL } from '../alert-constants';
import { createActionLink } from '../alert-utils';
import styles from './banner-alert.module.scss';
export var BannerAlertType;
(function (BannerAlertType) {
    BannerAlertType["WARNING"] = "warning";
    BannerAlertType["ERROR"] = "error";
    BannerAlertType["SUCCESS"] = "success";
    BannerAlertType["INFO"] = "info";
})(BannerAlertType || (BannerAlertType = {}));
export const BannerAlert = ({ message, isVisible, type = BannerAlertType.WARNING, showCloseButton = true, onClose = _.noop, link, secondaryLink, primaryAction, secondaryAction, global = false, className = '', customIconID, shouldAnnounceAlert, closeOnUnmount, collectEvent, errorObj, ariaLabel, isClickable, ...restProps }) => {
    const [height, setHeight] = useState(0);
    const { embedParams } = useEmbedContext();
    /*
     * If content is multiline for Section Banner Alert (Local Alert), setting the height of container to add extra styling
     * on Icon, Button and Link.
     */
    const bannerRef = useCallback((element) => {
        if (element) {
            setHeight(element.clientHeight);
        }
    }, []);
    const hideAlertInFullAppEmbed = (embedParams === null || embedParams === void 0 ? void 0 : embedParams.isFullAppEmbed) && (embedParams === null || embedParams === void 0 ? void 0 : embedParams.showAlerts) === false;
    const localOrGlobal = global === true ? 'global' : 'local';
    const multiLineAndLocal = height > 60 && !global; // To check if Alert is local and on multi-line.
    const colors = {
        [BannerAlertType.WARNING]: IconColor.YELLOW,
        [BannerAlertType.ERROR]: IconColor.RED,
        [BannerAlertType.SUCCESS]: IconColor.GREEN,
        [BannerAlertType.INFO]: IconColor.BLUE,
    };
    useEffect(() => {
        if (isVisible && type === BannerAlertType.ERROR) {
            const genericError = new Error(message);
            genericError.name = 'TSError';
            trackException(genericError);
            if (collectEvent) {
                collectEvent(new UserAction(userActionTypeEnum.ERROR_MESSAGE_SHOWN), {
                    errorType: type,
                    errorText: message,
                    global,
                    currentUrl: window.location.href,
                    errorObject: errorObj,
                    orgId: getCurrentOrgId(),
                    userGUID: getUserGuid(),
                    privileges: getUserPrivileges(),
                });
            }
        }
    }, [errorObj, global, isVisible, message, type, collectEvent]);
    const iconColor = colors[type];
    const renderLink = (actionLink) => {
        const linkStyles = cx(styles.link, {
            [styles.linkInMultiline]: multiLineAndLocal,
        });
        return actionLink ? createActionLink(actionLink, linkStyles) : null;
    };
    const renderMessage = (messageBody) => {
        if (_.isString(messageBody)) {
            return (React.createElement("div", { className: styles.contentSub },
                React.createElement(Typography, { variant: "body-normal", noMargin: true }, messageBody)));
        }
        return React.createElement("div", { className: styles.contentSub }, messageBody);
    };
    const primaryActionButton = primaryAction ? (React.createElement(Button, { text: primaryAction.label, type: "primary", ariaLabel: primaryAction.label, size: "s", backgroundType: "colored", className: cx({
            [styles.primaryActionInMultiline]: multiLineAndLocal,
        }), onClick: primaryAction.onClick, isDisabled: primaryAction.isDisabled })) : null;
    const secondaryActionButton = secondaryAction ? (React.createElement(Button, { text: secondaryAction.label, type: "secondary", ariaLabel: secondaryAction.label, size: "s", backgroundType: "light", className: styles.secondaryActionInMultiline, onClick: secondaryAction.onClick })) : null;
    const containerClass = cx(styles.container, styles[type], styles[localOrGlobal], className, {
        [styles.noCloseButton]: !showCloseButton,
    });
    if (!isVisible || !message) {
        return null;
    }
    let iconID = IconID.EXCLAMATION_POINT_CIRCLE;
    if (customIconID) {
        iconID = customIconID;
    }
    else if (type === BannerAlertType.SUCCESS) {
        iconID = IconID.CHECKMARK_CIRCLE;
    }
    if (hideAlertInFullAppEmbed) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement("div", { className: containerClass, ...restProps, ref: bannerRef, role: "alert", 
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex: isClickable ? 0 : -1, "aria-label": ariaLabel, ...(shouldAnnounceAlert
            ? {
                'aria-live': 'assertive',
                'aria-atomic': 'true',
            }
            : {}) },
        React.createElement(Icon, { id: iconID, size: IconSize.LARGE, color: iconColor, className: cx(styles.icon, {
                [styles.iconInMultiline]: multiLineAndLocal,
            }) }),
        React.createElement("div", { className: styles.content },
            renderMessage(message),
            renderLink(link),
            renderLink(secondaryLink),
            primaryActionButton,
            secondaryActionButton),
        showCloseButton && (React.createElement(Button, { size: global ? 'm' : 's', icon: IconID.CROSS, type: "secondary", ariaLabel: CLOSE_BTN_ARIA_LABEL, onClick: onClose, className: styles.closeButton }))));
};
//# sourceMappingURL=banner-alert.js.map